<template>
  <div
    id="box-container"
  >
    <div
      id="img-container"
      class="rounded"
      :style="{'aspect-ratio': !isCroppingImage ? aspectRatio : 'auto' }"
      @mouseover="isHoveringOverImage = true"
      @mouseleave="isHoveringOverImage = false"
    >
      <img
        v-if="!isCroppingImage"
        class="rounded"
        :src="displayImageSrc"
        alt="Display Image"
        width="100%"
        :style="{'aspect-ratio': aspectRatio}"
      >
      <vue-cropper
        v-else
        ref="cropper"
        :src="displayImageSrc"
        alt="Display Image"
        :view-mode="2"
        :initial-aspect-ratio="aspectRatio"
        :aspect-ratio="aspectRatio"
      />
      <div
        v-show="shouldDisplayImageControls"
        id="img-controls"
        class="m-2"
      >
        <b-button
          ref="display-image-delete-btn"
          variant="danger"
          class="mr-2"
          :class="{ 'btn-loading': isDeleteButtonLoading }"
          @click="deleteImage()"
          @focus="setIsImageControlFocused(true)"
          @blur="setIsImageControlFocused(false)"
          :disabled="GET_IS_CAMPAIGN_READONLY"
        >
          <i
            class="fal fa-image-slash"
          />
        </b-button>
        <b-button
          ref="display-image-delete-btn"
          variant="info"
          @click="setIsCroppingImage(true)"
          :disabled="GET_IS_CAMPAIGN_READONLY"
        >
          <i class="fal fa-crop" />
        </b-button>
      </div>
    </div>
    <template
      v-if="isCroppingImage"
    >
      <div
        class="d-flex mt-2"
      >
        <b-button
          variant="tertiary"
          class="ml-auto mr-1"
          @click="setIsCroppingImage(false)"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :class="{ 'btn-loading': isSaveButtonLoading }"
          @click="cropImage"
          :disabled="GET_IS_CAMPAIGN_READONLY"
        >
          Save
        </b-button>
      </div>
    </template>
  </div>
</template>
<script>

import VueCropper from 'vue-cropperjs';
import {
  mapActions, mapGetters, mapState,
} from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import 'cropperjs/dist/cropper.css';

function initialData() {
  return {
    isHoveringOverImage: false,
    isCroppingImage: false,
    isSaveButtonLoading: false,
    isDeleteButtonLoading: false,
    isImgControlInFocus: false,
  };
}

export default {
  name: 'CurrentAsset',
  components: {
    VueCropper,
  },
  props: {
    imagePath: {
      type: String,
      default: null,
    },
    aspectRatio: {
      type: Number,
      default: 16 / 9,
    },
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
      'creativeTypeSelected',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    displayImageSrc() {
      return this.imagePath || '/images/image-not-found.png';
    },
    shouldDisplayImageControls() {
      if (!this.imagePath) return false;
      return this.isImgControlInFocus || (this.isHoveringOverImage && !this.isCroppingImage);
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.UPLOAD_CREATIVE_DISPLAY_IMAGE,
      contentTypes.DELETE_CREATIVE_DISPLAY_IMAGE,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    setIsCroppingImage(isCropping) {
      this.$emit('update:isCroppingImage', isCropping);
      this.isCroppingImage = isCropping;
      if (isCropping) this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      else this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
    },
    setIsImageControlFocused(isFocused) {
      this.isImgControlInFocus = isFocused;
      if (isFocused) this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      else this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
    },
    async deleteImage() {
      if (this.GET_IS_CAMPAIGN_READONLY) return;
      this.isDeleteButtonLoading = true;
      const payload = {
        creativeId: this.selectedCreativeId,
        contentItemUuid: this.selectedArticleUuid,
        imageType: this.creativeTypeSelected === 'IMAGE' ? 'CREATIVE' : 'IMAGE',
      };
      await this.DELETE_CREATIVE_DISPLAY_IMAGE(payload);
      this.TRIGGER_CREATIVE_RERENDER();
      this.isDeleteButtonLoading = false;
    },
    async cropImage() {
      if (this.GET_IS_CAMPAIGN_READONLY) return;
      this.isSaveButtonLoading = true;
      const cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL('image/jpeg');
      const block = cropImg.split(';');
      const contentType = block[0].split(':')[1];
      const fileType = contentType.split('/')[1];
      const realData = block[1].split(',')[1];
      const blob = this.b64toBlob(realData, contentType);

      const formData = new FormData();
      formData.append('file', blob, `image.${fileType}`);
      formData.append('body', new Blob([JSON.stringify({ contentItemUuid: this.selectedArticleUuid })], { type: 'application/json' }));

      const payload = {
        creativeId: this.selectedCreativeId,
        formData,
      };
      await this.UPLOAD_CREATIVE_DISPLAY_IMAGE(payload);
      this.setIsCroppingImage(false);
      this.isSaveButtonLoading = false;
    },
    b64toBlob(b64Data, contentType = '', sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, { type: contentType });
    },
  },
  watch: {
    imageFileName(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isCroppingImage = false;
        this.isImgControlInFocus = false;
      }
    },
  },
};
</script>
<style scoped lang="scss">
#box-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
#img-container {
  position: relative;
  border-radius: $border-radius;
  img {
    object-fit: cover;
  }
}
#img-controls {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
